.accordion {
    display: flex;
    flex-direction: column;
    width: 100%;
}

/* Reset UA-default vertical margin on the heading wrapping the trigger.
   RAC's <Heading> renders a real <h1>…<h6>; browser stylesheets apply
   block margins that visually band-gap above and below the disclosure
   header. */
.accordion :where(h1, h2, h3, h4, h5, h6) {
    margin: 0;
}

.header {
    /* Reset UA button defaults — same load-bearing 4 per
       feedback_subtle_button_reset (the rest are cargo-cult). */
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;

    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    min-height: 48px;
    padding-inline: var(--spacing-md);
    /* Consumers can pull the header outward past their container's inset by
       setting `--disclosure-header-margin-inline` on a parent (e.g., a popover
       host that wants disclosure backgrounds to bleed to the popover edge).
       Defaults to 0 — primitive stays neutral. Same opt-in pattern as
       BaseMenu's `--menu-item-button-margin-inline`. The width is widened by
       the same amount so the bleed applies on BOTH sides — negative margin
       alone would only shift the element, not grow it. */
    margin-inline: var(--disclosure-header-margin-inline, 0);
    width: calc(100% - 2 * var(--disclosure-header-margin-inline, 0px));
    outline: none;
    text-align: start;
    font-family: var(--fontFamilySansSemiBold), sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.25;
    color: var(--colorTextPrimary);
    border-radius: 4px;
    transition: background-color 150ms ease;
}

.header[data-hovered] {
    background-color: rgba(0, 0, 0, 0.1);
}

.header[data-pressed] {
    background-color: rgba(0, 0, 0, 0.2);
}

.header[data-focus-visible] {
    outline: 1px solid var(--colorBorderAccent);
    outline-offset: 2px;
}

.header[data-disabled] {
    opacity: 0.4;
    cursor: default;
}

.chevron {
    flex-shrink: 0;
    transition: transform 150ms ease;
}

.header[aria-expanded='true'] .chevron {
    transform: rotate(180deg);
}

.title {
    flex: 1;
    min-width: 0;
    /* Font defaults inherit from .header so plain-string titles match the
       Figma `Product/Heading small strong` style. Consumers passing a rich
       title node (e.g., wrapping in UtilityText) override these. */
}

/* Animate open/closed via the grid-template-rows 0fr/1fr trick — a single
   grid item collapses from 1fr to 0fr smoothly, dragging the inner content's
   visible height down with it. Padding on the inner is also animated so the
   collapsed panel takes zero vertical space. `visibility` snaps to hidden
   AFTER the height transition completes (delayed transition) so focusable
   children get pulled out of the tab order when the section is collapsed.
   RAC's `useDisclosure` sets `aria-hidden` on the panel — that's the driver.
   Easing uses `--spring-1` from tokens.css for parity with BasePopover's
   slide-in motion. */
.panel {
    display: grid;
    grid-template-rows: 0fr;
    visibility: hidden;
    transition:
        grid-template-rows 500ms var(--spring-1),
        visibility 0s linear 500ms;
}

.panelInner {
    min-height: 0;
    overflow: hidden;
    /* 0 vertical padding so it collaspe completely
    but we keep the horizontal one so it doesn't jump*/ 
    padding: 0 var(--spacing-md);
    transition: padding 500ms var(--spring-1);
}

.panel[aria-hidden='false'] {
    grid-template-rows: 1fr;
    visibility: visible;
    transition:
        grid-template-rows 500ms var(--spring-1),
        visibility 0s linear 0s;
}

.panel[aria-hidden='false'] .panelInner {
    padding: var(--spacing-md);
}

@media (prefers-reduced-motion: reduce) {
    .panel,
    .panelInner {
        transition: none;
    }
}

/* ── Highlighted variant ──────────────────────────────────────────────── */

.accordion[data-highlighted] {
    border-radius: 4px;
    border: 1px solid var(--colorBorderPrimary);
}

.accordion[data-highlighted] .header {
    background-color: var(--colorSurfaceHighlight);
    /* 3px so the background aligns nicely with the boder of the parent component*/
    border-radius: 3px;
}

.accordion[data-highlighted] .header[data-hovered] {
    background-color: color-mix(in srgb, var(--colorSurfaceHighlight) 90%, #000);
}

.accordion[data-highlighted] .header[data-pressed] {
    background-color: color-mix(in srgb, var(--colorSurfaceHighlight) 80%, #000);
}

.accordion[data-highlighted] .header[aria-expanded='true'] {
    background-color: var(--colorSurfaceHighlightShade1);
    border-radius: 3px 3px 0 0;
}

.accordion[data-highlighted] .panel {
    background-color: var(--colorSurfaceHighlightShade1);
    border-top: 1px solid var(--colorBorderPrimary);
    border-radius: 0 0 3px 3px;
}

.accordion[data-highlighted] .panel[aria-hidden='true'] {
    border-top: 0;
}
